import { useState, useCallback } from "react";

import { readItems } from "@directus/sdk";
import { message } from "antd";
import { getDirectusClient } from "../../../../lib/directus";

export function useLoans(vendor) {
  const [loans, setLoans] = useState([]);
  const [loadingLoans, setLoadingLoans] = useState(false);

  const getLoans = useCallback(async () => {
    if (!vendor?.id) {
      setLoans([]);
      return;
    }
    setLoadingLoans(true);
    let directus = await getDirectusClient();
    try {
      const res = await directus.request(
        readItems("prestamos", {
          fields: [
            "id",
            "tipo",
            "estado",
            "vendedor",
            "inventario_nsp.*",
            "inventario_nsp.inventario_ns_prestamo_id.*",
            "inventario_nsp.inventario_ns_prestamo_id.inventario_ns.*",
            "inventario_nsp.inventario_ns_prestamo_id.inventario_ns.producto.*",
            "inventario_nsp.inventario_ns_prestamo_id.inventario_ns.producto.grupo.impuesto",
            "inventario_nsp.inventario_ns_prestamo_id.inventario_ns.producto.preciosinimp",
          ],
          filter: {
            _and: [
              { vendedor: { _eq: vendor.id } },
              { tipo: { _eq: "prestamo" } },
              { estado: { _eq: "activo" } },
            ],
          },
        })
      );
      if (Array.isArray(res) && res.length > 0) {
        const allProducts = [];
        const loanIds = res.map((loan) => loan.id);

        // Obtener todos los seriales de todos los préstamos en una sola petición
        const allSerials = await directus.request(
          readItems("seriales", {
            fields: [
              "producto.id",
              "producto.nombre",
              "producto.preciosinimp",
              "producto.grupo.impuesto",
              "prestamo.prestamos_id.id",
            ],
            filter: {
              prestamo: {
                prestamos_id: {
                  id: {
                    _in: loanIds,
                  },
                },
              },

              estado: { _eq: 3 },
              user: { _eq: vendor.id },
            },
            limit: -1,
          })
        );

              
         const serialsByLoan = {};
         allSerials.forEach((serial) => {
           // Manejar el array de préstamos
           let loanId = null;
           if (Array.isArray(serial.prestamo)) {
             // Buscar el préstamo que no sea null
             const validPrestamo = serial.prestamo.find(p => p.prestamos_id?.id);
             loanId = validPrestamo?.prestamos_id?.id;
           } else {
             // Caso anterior donde prestamo es un objeto único
             loanId = serial.prestamo?.prestamos_id?.id;
           }
           
           const productId = serial.producto?.id;

           if (loanId && productId) {
             if (!serialsByLoan[loanId]) {
               serialsByLoan[loanId] = {};
             }
             if (!serialsByLoan[loanId][productId]) {
               serialsByLoan[loanId][productId] = {
                 count: 0,
                 product: serial.producto,
               };
             }
             serialsByLoan[loanId][productId].count++;
           }
         });

        // Objeto para consolidar productos por productId y tipo
        const consolidatedProducts = {};

        // Procesar cada préstamo con los datos ya agrupados
        for (const loan of res) {
          const loanSerials = serialsByLoan[loan.id] || {};

          // Agregar productos serializados
          for (const productId in loanSerials) {
            const productData = loanSerials[productId];
            const key = `${productId}_serialized`;
            
            if (consolidatedProducts[key]) {
              // Si ya existe, sumar cantidad
              consolidatedProducts[key].quantity += productData.count;
              consolidatedProducts[key].loanIds.push(loan.id);
            } else {
              // Si no existe, crear nuevo
              consolidatedProducts[key] = {
                product: productData.product.nombre,
                quantity: productData.count,
                price: parseFloat(productData.product.preciosinimp || 0),
                tax: parseFloat(productData.product.grupo?.impuesto || 0),
                productId,
                loanId: loan.id, // Primer préstamo donde aparece
                loanIds: [loan.id], // Array de todos los préstamos donde aparece
                isNotSerialized: false,
              };
            }
          }

          // Agregar productos no serializados
          loan.inventario_nsp?.forEach((ns) => {
            if (ns.inventario_ns_prestamo_id?.estado === "prestado") {
              const productId = ns.inventario_ns_prestamo_id?.inventario_ns?.producto?.id;
              const key = `${productId}_nonserialized`;
              const quantity = parseInt(ns.inventario_ns_prestamo_id?.cantidad || 0);
              
              if (consolidatedProducts[key]) {
                // Si ya existe, sumar cantidad
                consolidatedProducts[key].quantity += quantity;
                consolidatedProducts[key].loanIds.push(loan.id);
              } else {
                // Si no existe, crear nuevo
                consolidatedProducts[key] = {
                  product:
                    ns.inventario_ns_prestamo_id?.inventario_ns?.nombre ||
                    "Producto desconocido",
                  quantity: quantity,
                  price: parseFloat(
                    ns.inventario_ns_prestamo_id?.inventario_ns?.producto
                      ?.preciosinimp || 0
                  ),
                  tax: parseFloat(
                    ns.inventario_ns_prestamo_id?.inventario_ns?.producto?.grupo
                      ?.impuesto || 0
                  ),
                  productId: productId,
                  loanId: loan.id, // Primer préstamo donde aparece
                  loanIds: [loan.id], // Array de todos los préstamos donde aparece
                  isNotSerialized: true,
                };
              }
            }
          });
        }

        // Convertir el objeto consolidado a array
        Object.values(consolidatedProducts).forEach(product => {
          allProducts.push(product);
        });
        const finalProducts = allProducts.map((product) => {
          const subtotal = product.quantity * product.price;
          const tax = (subtotal * product.tax) / 100;
          return {
            ...product,
            partialTotal: (subtotal + tax).toFixed(2),
            key: `${product.productId}_${product.isNotSerialized ? 'ns' : 's'}`,
          };
        });
        if (finalProducts.length === 0) {
          message.info(
            `${vendor.first_name} ${vendor.last_name} no tiene préstamos pendientes`
          );
        }
        setLoans(finalProducts);
      } else {
        setLoans([]);
        if (vendor.id) {
          message.info(
            `${vendor.first_name} ${vendor.last_name} no tiene préstamos pendientes`
          );
        }
      }
    } catch (err) {
      console.error("Error in getLoans:", err);
      message.error("Error al consultar los préstamos");
      setLoans([]);
    } finally {
      setLoadingLoans(false);
    }
  }, [vendor]);

  return { loans, loadingLoans, getLoans };
}
